/**
 * Application multi evenement e.labriquerie.net.
 * src/i18n/fr.cvRegles.ts
 *
 * Messages de la RÈGLE UNIQUE d'accès aux CV (décision de Mika du
 * 2026-10-04 : « c'est le choix du back-office qui prime ») :
 *   - l'écran de réglage du back-office (components/admin/CvReglesPanel) ;
 *   - les cases que le candidat coche au dépôt et dans son profil
 *     (components/cv/CvVisibilityChoice) ;
 *   - les mentions côté entreprise (portée « au choix du candidat »,
 *     visionneuse et points forts coupés).
 * Fusionné dans fr.ts.
 *
 * Plateforme multi-événements : aucun nom, lieu, ni type d'événement écrit
 * en dur. Le type passe par {eventTypeOf}.
 *
 * Mikael - chef de projet - 2026-10-04
 */
export const messagesCvReglesFr: Record<string, string> = {
  // ── Back-office : la règle ─────────────────────────────────────────────
  'cvRegles.titre': 'Qui voit les CV des candidats',
  'cvRegles.intro':
    'Cette règle s’applique partout où une entreprise voit quelque chose d’un CV : CV reçus, téléchargement, lecture dans l’application, points forts au scan, recherche premium et coordonnées. Le candidat ne choisit que dans ce que vous lui laissez, il ne peut jamais aller au-delà.',
  'cvRegles.portee.titre': 'Quelles entreprises voient le CV d’un candidat',
  'cvRegles.portee.scanned': 'Seulement les entreprises qui ont rencontré le candidat',
  'cvRegles.portee.scanned.aide':
    'Une entreprise voit le CV quand elle a scanné le badge du candidat, ou quand le candidat lui a envoyé sa candidature. Le candidat n’a rien à choisir ici.',
  'cvRegles.portee.choice': 'Les mêmes, et toutes les entreprises si le candidat le souhaite',
  'cvRegles.portee.choice.aide':
    'En plus, chaque candidat peut cocher « J’ouvre mon CV à toutes les entreprises ». La case n’est jamais cochée d’avance et se décoche à tout moment.',
  'cvRegles.portee.all': 'Toutes les entreprises voient tous les CV partagés',
  'cvRegles.portee.all.aide':
    'Imposé par l’organisation : toute entreprise connectée lit tout CV partagé, même sans avoir rencontré le candidat. La case de partage le dit au candidat au moment du dépôt. À réserver aux événements où c’est l’usage annoncé.',

  'cvRegles.candidature.titre': 'Candidatures en ligne',
  'cvRegles.candidature.libelle': 'Les candidats peuvent envoyer leur CV à une entreprise ou à une offre',
  'cvRegles.candidature.aide':
    'L’entreprise choisie voit alors le CV, même sans scan et même si le candidat ne le partage pas au scan. Éteindre empêche les nouvelles candidatures ; celles déjà envoyées restent ouvertes à l’entreprise qui les a reçues.',

  'cvRegles.cvtheque.titre': 'Recherche premium dans les CV (CVthèque)',
  'cvRegles.cvtheque.off': 'Seulement les CV déjà ouverts à l’entreprise',
  'cvRegles.cvtheque.off.aide':
    'Une entreprise premium ne cherche que parmi les candidats qu’elle a scannés ou qui lui ont candidaté. Aucune case n’est proposée aux candidats.',
  'cvRegles.cvtheque.volontaires': 'Aussi les CV des candidats volontaires',
  'cvRegles.cvtheque.volontaires.aide':
    'Chaque candidat voit une case « J’accepte que les entreprises premium recherchent mon CV », au dépôt du CV et dans son profil. Il la décoche quand il veut : son CV sort aussitôt de la recherche. Chaque accord et chaque retrait est daté et journalisé.',
  'cvRegles.cvtheque.jamaisPrecoche':
    'Cette case n’est jamais cochée d’avance : un accord pré-coché n’est pas un consentement.',
  'cvRegles.cvtheque.moduleEteint':
    'Le module premium est éteint : ce réglage n’a pas d’effet pour l’instant et aucune case n’est montrée aux candidats. Le module s’active dans « Premium : réglages et salles ».',
  'cvRegles.cvtheque.moduleType':
    'Le module premium n’existe pas pour ce type d’événement : ce réglage n’a pas d’effet.',

  'cvRegles.fonctions.titre': 'Fonctions liées au CV',
  'cvRegles.pointsForts': 'Montrer les points forts du CV au scan',
  'cvRegles.pointsForts.aide':
    'La fiche d’entretien résume le CV (dernier poste, diplôme, permis, langues) quand le CV est ouvert à l’entreprise. Éteint, la fiche l’indique et ne lit rien du CV.',
  'cvRegles.visionneuse': 'Lire le CV dans l’application',
  'cvRegles.visionneuse.aide':
    'Un recruteur ouvre le CV sur son téléphone sans le télécharger. Éteint, seul le téléchargement de l’original reste possible, avec les mêmes droits.',
  'cvRegles.memorises': 'Garder les points mémorisés quand le CV est retiré',
  'cvRegles.memorises.aide':
    'Les points forts qu’un recruteur a mémorisés dans une fiche d’entretien restent lisibles si le candidat retire, supprime ou ne partage plus son CV. Éteint, ils sont masqués dès que le CV n’est plus accessible.',
  'cvRegles.ia.titre': 'Intelligence artificielle et CV',
  'cvRegles.ia.aide':
    'Les conseils au candidat sur son CV, la lecture des CV déposés en photo et l’enrichissement des points forts se règlent fonction par fonction dans l’écran IA (marche ou arrêt, fournisseur, budget). Les comptes mineurs n’y ont jamais accès.',
  'cvRegles.ia.lien': 'Ouvrir les réglages IA',

  'cvRegles.apercu.titre': 'Aperçu',
  'cvRegles.apercu.phrase':
    'Avec ce réglage, une entreprise non premium qui n’a pas scanné le candidat : {resultat}.',
  'cvRegles.apercu.intro': 'Pour un candidat dont le CV est déposé et partagé :',
  'cvRegles.apercu.situation': 'Entreprise',
  'cvRegles.apercu.sansAccord': 'Candidat qui n’a rien coché',
  'cvRegles.apercu.avecAccord': 'Candidat qui a tout accepté',
  'cvRegles.apercu.situation.nonPremiumSansScan': 'Non premium, ne l’a pas scanné',
  'cvRegles.apercu.situation.premiumSansScan': 'Premium, ne l’a pas scanné',
  'cvRegles.apercu.situation.scan': 'A scanné son badge',
  'cvRegles.apercu.situation.candidature': 'A reçu sa candidature (CV non partagé au scan)',
  'cvRegles.apercu.situation.mineur': 'Premium, candidat mineur sans accord parental',
  'cvRegles.apercu.rien': 'ne voit pas le CV',
  'cvRegles.apercu.ouvrir': 'ouvre le CV',
  'cvRegles.apercu.liste': 'le voit dans ses CV reçus',
  'cvRegles.apercu.recherche': 'le trouve par la recherche premium',
  'cvRegles.apercu.memeChose': 'Le candidat n’a rien à choisir avec ce réglage.',

  'cvRegles.enregistrer': 'Enregistrer la règle',
  'cvRegles.annuler': 'Annuler les modifications',
  'cvRegles.enregistre': 'Règle d’accès aux CV enregistrée.',
  'cvRegles.erreur': 'La règle n’a pas pu être enregistrée. Réessayez.',
  'cvRegles.rejet':
    'Certains réglages manquent sur cette instance (migration V138 à appliquer) : {cles}.',
  'cvRegles.premium.titre': 'Dans quels CV les entreprises premium cherchent',
  'cvRegles.premium.lien': 'Voir toute la règle d’accès aux CV',

  // ── Candidat : ce qu'il peut décider ───────────────────────────────────
  'cvRegles.candidat.titre': 'Qui peut voir mon CV ?',
  'cvRegles.candidat.base.scanned':
    'Les entreprises qui scannent votre badge, et celles à qui vous envoyez votre candidature, voient votre CV s’il est partagé.',
  'cvRegles.candidat.base.all':
    'L’organisation a ouvert les CV partagés à toutes les entreprises {eventTypeOf}, même celles que vous ne rencontrerez pas.',
  'cvRegles.candidat.cvtheque':
    'J’accepte que les entreprises premium {eventTypeOf} recherchent mon CV et me proposent un entretien',
  'cvRegles.candidat.cvtheque.aide':
    'Facultatif, jamais coché d’avance. Décochez à tout moment : votre CV sort aussitôt de la recherche. Chaque consultation reste inscrite dans votre journal.',
  'cvRegles.candidat.tous':
    'J’ouvre mon CV à toutes les entreprises {eventTypeOf}, même celles que je ne rencontrerai pas',
  'cvRegles.candidat.tous.aide': 'Facultatif, jamais coché d’avance, à décocher quand vous voulez.',
  'cvRegles.candidat.tous.inclut': 'Compris dans l’ouverture à toutes les entreprises.',

  // ── Entreprise ─────────────────────────────────────────────────────────
  'cvRegles.entreprise.portee.choice':
    'Vous voyez les CV des personnes rencontrées sur votre stand, et ceux des candidats qui ont ouvert leur CV à toutes les entreprises.',
  'cvRegles.entreprise.visionneuseDesactivee':
    'L’organisation a choisi de ne pas afficher les CV dans l’application. Téléchargez l’original pour le lire.',
  'cvRegles.entreprise.pointsDesactives':
    'L’organisation n’affiche pas les points forts des CV au scan.',
};
